import Joi from 'joi';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import animations from 'utils/animations';
import emailjs from '@emailjs/browser';
import Seo from 'components/Seo';
import AnimationPage from 'components/AnimationPage';
import ErrorMessage from 'components/ErrorMessage';
import { ReactComponent as Spinner } from 'assets/images/spinner.svg';
import { useState } from 'react';
import "assets/styles/Contact.css";


export default function Contact() {
  const { opacity } = animations.components;
  const [status, setStatus] = useState('idle');

  const schema = Joi.object({
    subject: Joi.string().required(),
    email: Joi.string().email({ tlds: false }).required(),
    firstName: Joi.string().required(),
    lastName: Joi.string().required(),
    message: Joi.string().required(),
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: joiResolver(schema)
  });

  const onSubmit = data => {
    setStatus('loading')

    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      data, {
      publicKey: process.env.REACT_APP_EMAILJS_API_KEY
    }
    ).then(
      (response) => {
        setStatus('success');
      },
      (err) => {
        console.error(err)
      }
    )
  }

  return (
    <AnimationPage>
      <Seo title='Contact - Kollectiff' />
      <main>
        <div className="container contact">
          <motion.h1
            className="k-title-sm md:k-title-md lg:k-title-lg"
            {...opacity}
            transition={{ delay: 0.2 }}
          >Get in Touch
          </motion.h1>
          
          

          <AnimatePresence mode="wait">
            {status !== 'success' && (
              <motion.form 
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.3 }}
                onSubmit={handleSubmit(onSubmit)}
                className='contact-form'>
                  <motion.p
                    className="k-text-md lg:k-text-lg"
                    {...opacity}
                    transition={{ delay: 0.2 }}
                  >All fields are required.
                  </motion.p>
                  <div className='contact-form-row'>
                    <motion.div
                      {...opacity}
                      className="form-group">
                      <label htmlFor="subject" className='k-text-md lg:k-text-lg'>Subject</label>
                      <select id="subject" {...register('subject')}>
                        <option value="">Select an option</option>
                        <option value="brand">Brand</option>
                        <option value="agency">Agency</option>
                        <option value="press">Press</option>
                        <option value="careers">Careers</option>
                      </select>
                      <ErrorMessage message="Subject is required" error={errors.subject?.message} />
                    </motion.div>
                    <motion.div
                      {...opacity}
                      transition={{ delay: .2 }}
                      className="form-group">
                      <label htmlFor="email" className='k-text-md lg:k-text-lg'>Email</label>
                      <input type="email" id="email" name="email" placeholder="Enter your email" {...register('email')} />
                      <ErrorMessage message="Email is required" error={errors.email?.message} />
                    </motion.div>
                  </div>
                  <div className='contact-form-row'>
                    <motion.div
                      {...opacity}
                      transition={{ delay: .3 }}
                      className="form-group">
                      <label htmlFor="firstName" className='k-text-md lg:k-text-lg'>First Name</label>
                      <input type="text" id="firstName" name="firstName" placeholder="Enter your First Name" {...register('firstName')} />
                      <ErrorMessage message="First name is required" error={errors.firstName?.message} />
                    </motion.div>
                    <motion.div
                      {...opacity}
                      transition={{ delay: .4 }}
                      className="form-group">
                      <label htmlFor="lastName" className='k-text-md lg:k-text-lg'>Last Name</label>
                      <input type="text" id="lastName" name="lastName" placeholder="Enter your Last Name" {...register('lastName')} />
                      <ErrorMessage message="Last name is required" error={errors.lastName?.message} />
                    </motion.div>
                  </div>
                  <div className='contact-form-row'>
                    <motion.div
                      {...opacity}
                      transition={{ delay: .5 }}
                      className="form-group form-group-message">
                      <label htmlFor="message" className='k-text-md lg:k-text-lg'>Message</label>
                      <textarea id="message" name="message" placeholder="We're excited to hear from you" {...register('message')} />
                      <ErrorMessage message="Message is required" error={errors.message?.message} />
                    </motion.div>
                  </div>
                  <motion.div
                      {...opacity}
                      transition={{ delay: .6 }}
                      className="contact-footer">
                      <motion.button
                        className='btn-lime'
                        type="submit"
                        whileTap={{ scale: 0.8 }}
                        disabled={status === 'loading'}>
                        <AnimatePresence mode="wait">
                          {status === 'idle' && (
                            <motion.span
                              key="submit"
                              initial={{ opacity: 0, y: -20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 20 }}>
                              Get in touch</motion.span>
                          )}
                          {status === 'loading' && (
                            <motion.span
                              key="spinner"
                              initial={{ opacity: 0, y: -20 }}
                              animate={{ opacity: 1, y: 0 }}>
                              <Spinner />
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </motion.button>
                      
                      <div className="email">
                        Or email us at <a href="mailto:hello@kollectiff.com" className="k-text-md font-semibold link-animated">hello@kollectiff.com</a>
                      </div>
                  </motion.div>
              </motion.form>
            )}
            {status === 'success' && (
              <motion.div 
                key="message"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col gap-6">
                <span className="k-title-xs md:k-title-sm lg:k-title-md">Thanks for contacting us!</span>
                <span className="k-text-md">We have received your message. We’ll reach you out immediately!</span>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </main>
    </AnimationPage>
  );
}
